import { SectionApi } from '@/api';
import { toStoreError } from '@/types/storeError.interface';
import { AppDispatch } from '..';
import {
  backgroundSectionFailure,
  fetchSectionFailure,
  fetchSectionSuccess,
  sectionLoading,
  fetchSectionSummarySuccess,
  fetchApplicationSummarySuccess,
} from '../slices';
import {
  adaptFormItemArrayResponseToFormFields,
  adaptSectionResponseToSection,
  adaptGetSummaryResponseToSectionSummary,
  adaptGetSectionSummaryResponseToApplicationSummary,
} from '@/adapters';
import { updateFieldFailedAlert } from '@/services/alert';
import { IFormBase } from '@/forms/typings';
import { fetchApplication } from './applicationThunk';
import {
  notifyFetched,
  tookEffectDespite,
  type GroupActionOutcome,
  type OnFormFetched,
} from './groupActionOutcome';
import {
  fetchSectionFormSuccess,
  removeStagedFieldsGroup,
  setStagedFieldsGroup,
} from '@/forms/store';
import {
  UpdateSectionRequestDTO,
  UpdateStagedFieldsGroupRequestDTO,
  UpdateSectionConditionalFieldsGroupRequestDTO,
} from '@/api/section/DTOs';

/** Options accepted by fetches that can run behind an already-rendered section. */
export type SectionFetchOptions = {
  background?: boolean;
  /** True once a later section switch owns the skeleton; the response then lands as a background one. */
  isSuperseded?: () => boolean;
  onFetched?: OnFormFetched;
};

export const fetchSection =
  (
    applicationId: number,
    sectionId: number,
    options: SectionFetchOptions = {},
  ) =>
  async (dispatch: AppDispatch) => {
    // Background refreshes deliberately leave `loading` alone: flipping it swaps the form
    // for a skeleton, which remounts DynamicForm and resets the reinitialize counter.
    if (!options.background) {
      dispatch(sectionLoading());
    }

    const result = await new SectionApi().getSection({
      applicationId,
      sectionId,
    });

    // Checked after the await: a switch started while this GET was in flight owns
    // the skeleton, and a foreground response would lower it over stale cached values.
    const background =
      options.background || options.isSuperseded?.() === true;

    if (!result.success) {
      notifyFetched(options.onFetched, undefined);
      const error = toStoreError(result.error);
      dispatch(
        background ? backgroundSectionFailure(error) : fetchSectionFailure(error),
      );
      return;
    }

    const response = result.data;
    const formFields = adaptFormItemArrayResponseToFormFields(response.items);
    notifyFetched(options.onFetched, formFields);
    dispatch(
      fetchSectionSuccess({
        ...adaptSectionResponseToSection(response),
        background,
      }),
    );

    dispatch(fetchSectionFormSuccess({ sectionId, formFields }));
  };

export const updateSection =
  (
    applicationId: number,
    sectionId: number,
    formField: UpdateSectionRequestDTO['formField'],
  ) =>
  async (dispatch: AppDispatch) => {
    const result = await new SectionApi().updateSection({
      applicationId,
      sectionId,
      formField,
    });

    if (!result.success) {
      dispatch(backgroundSectionFailure(toStoreError(result.error)));
      return;
    }

    const response = result.data;
    if (!response.success) {
      updateFieldFailedAlert();
      dispatch(
        backgroundSectionFailure({
          code: 'UNK_001',
          message: `Failed to update form field ${formField.itemID}`,
          category: 'server',
        }),
      );
    } else {
      if (response.summary) {
        dispatch(
          fetchSectionSummarySuccess({
            id: sectionId,
            ...adaptGetSummaryResponseToSectionSummary(response.summary),
          }),
        );
        dispatch(
          fetchApplicationSummarySuccess({
            id: applicationId,
            ...adaptGetSectionSummaryResponseToApplicationSummary(
              response.summary,
            ),
          }),
        );
      }

      if (response.section) {
        dispatch(
          fetchSectionSuccess({
            ...adaptSectionResponseToSection(response.section),
            background: true,
          }),
        );
        dispatch(
          fetchSectionFormSuccess({
            sectionId,
            formFields: adaptFormItemArrayResponseToFormFields(
              response.section.items,
            ),
          }),
        );
        // The section is already applied — no follow-up fetch, so no remount.
        return false;
      }

      return response.reloadRequired;
    }
  };

export const addSectionGroup =
  (applicationId: number, sectionId: number, groupId: string) =>
  async (dispatch: AppDispatch) => {
    const result = await new SectionApi().addSectionGroup({
      applicationId,
      sectionId,
      groupId,
    });

    if (!result.success) {
      dispatch(fetchSectionFailure(toStoreError(result.error)));
      return;
    }

    dispatch(fetchSection(applicationId, sectionId));
  };

/** Removes a group entry, reporting to `outcome` whether the server took it. */
export const removeSectionGroup =
  (
    applicationId: number,
    sectionId: number,
    groupId: string,
    outcome?: GroupActionOutcome,
  ) =>
  async (dispatch: AppDispatch) => {
    const result = await new SectionApi().removeSectionGroup({
      applicationId,
      sectionId,
      groupId,
    });

    if (!result.success) {
      const refetch = (onFetched: OnFormFetched) =>
        fetchSection(applicationId, sectionId, { onFetched });
      if (await tookEffectDespite(result.error, dispatch, refetch, outcome)) return;
      dispatch(fetchSectionFailure(toStoreError(result.error)));
      return;
    }

    // False when the server found nothing to remove, and so removed nothing.
    if (result.data !== true) {
      updateFieldFailedAlert();
      dispatch(
        fetchSectionFailure({
          code: 'UNK_001',
          message: `Failed to remove group ${groupId}`,
          category: 'server',
        }),
      );
      return;
    }

    outcome?.onAccepted();
    dispatch(fetchSection(applicationId, sectionId));
  };

export const setStateStagedFieldsGroup =
  (
    fieldId: IFormBase['fieldId'],
    firstStageFields: Array<IFormBase['fieldId']>,
  ) =>
  (dispatch: AppDispatch) => {
    dispatch(
      setStagedFieldsGroup({
        fieldId,
        firstStageFieldsGroup: firstStageFields,
      }),
    );
  };

export const removeStateStagedFieldsGroup =
  (fieldId: IFormBase['fieldId']) => (dispatch: AppDispatch) => {
    dispatch(removeStagedFieldsGroup(fieldId));
  };

export const updateStagedFieldsGroup =
  (
    applicationId: number,
    sectionId: number,
    formField: UpdateStagedFieldsGroupRequestDTO['formField'],
  ) =>
  async (dispatch: AppDispatch) => {
    const result = await new SectionApi().updateStagedFieldsGroup({
      applicationId,
      sectionId,
      formField,
    });

    if (!result.success) {
      dispatch(backgroundSectionFailure(toStoreError(result.error)));
      return;
    }

    const response = result.data;
    if (!response.success) {
      updateFieldFailedAlert();
      dispatch(
        backgroundSectionFailure({
          code: 'UNK_001',
          message: `Failed to update StagedGroup ${formField.groupID}`,
          category: 'server',
        }),
      );
    } else {
      if (response.summary) {
        dispatch(
          fetchSectionSummarySuccess({
            id: sectionId,
            ...adaptGetSummaryResponseToSectionSummary(response.summary),
          }),
        );
        dispatch(
          fetchApplicationSummarySuccess({
            id: applicationId,
            ...adaptGetSectionSummaryResponseToApplicationSummary(
              response.summary,
            ),
          }),
        );
      }
      return response.reloadRequired;
    }
  };

/** Clears a group entry, reporting to `outcome` whether the server took it. */
export const clearSectionGroup =
  (
    applicationId: number,
    sectionId: number,
    groupId: string,
    outcome?: GroupActionOutcome,
  ) =>
  async (dispatch: AppDispatch) => {
    const result = await new SectionApi().clearSectionGroup(
      applicationId,
      sectionId,
      groupId,
    );

    if (!result.success) {
      const refetch = (onFetched: OnFormFetched) =>
        fetchSection(applicationId, sectionId, { onFetched });
      if (await tookEffectDespite(result.error, dispatch, refetch, outcome)) {
        dispatch(fetchApplication(applicationId));
        return;
      }
      dispatch(fetchSectionFailure(toStoreError(result.error)));
      return;
    }

    const response = result.data;
    if (!response.success) {
      updateFieldFailedAlert();
      dispatch(
        fetchSectionFailure({
          code: 'UNK_001',
          message: `Failed to clear group ${groupId}`,
          category: 'server',
        }),
      );
    } else {
      outcome?.onAccepted();
      dispatch(fetchSection(applicationId, sectionId));
      dispatch(fetchApplication(applicationId));
    }
  };

export const fetchSectionSummary =
  (applicationId: number, sectionId: number) =>
  async (dispatch: AppDispatch) => {
    const result = await new SectionApi().getSummary(applicationId, sectionId);

    if (!result.success) {
      dispatch(fetchSectionFailure(toStoreError(result.error)));
      return;
    }

    const response = result.data;
    dispatch(
      fetchSectionSummarySuccess({
        id: sectionId,
        ...adaptGetSummaryResponseToSectionSummary(response),
      }),
    );

    dispatch(
      fetchApplicationSummarySuccess({
        id: applicationId,
        ...adaptGetSectionSummaryResponseToApplicationSummary(response),
      }),
    );
  };

export const updateSectionConditionalFieldsGroup =
  (
    applicationId: number,
    sectionId: number,
    formField: UpdateSectionConditionalFieldsGroupRequestDTO['formField'],
  ) =>
  async (dispatch: AppDispatch) => {
    const result = await new SectionApi().updateSectionConditionalFieldsGroup({
      applicationId,
      sectionId,
      formField,
    });

    if (!result.success) {
      dispatch(backgroundSectionFailure(toStoreError(result.error)));
      return;
    }

    const response = result.data;
    if (!response.success) {
      updateFieldFailedAlert();
      dispatch(
        backgroundSectionFailure({
          code: 'UNK_001',
          message: `Failed to update ConditionalFieldsGroup ${formField.groupID}`,
          category: 'server',
        }),
      );
    } else {
      if (response.summary) {
        dispatch(
          fetchSectionSummarySuccess({
            id: sectionId,
            ...adaptGetSummaryResponseToSectionSummary(response.summary),
          }),
        );
        dispatch(
          fetchApplicationSummarySuccess({
            id: applicationId,
            ...adaptGetSectionSummaryResponseToApplicationSummary(
              response.summary,
            ),
          }),
        );
      }
      return response.reloadRequired;
    }
  };
